import { cn } from '@/utils/cn'
import { motion } from 'framer-motion'
import { Check } from 'lucide-react'

interface ProgressStep {
  id: string
  label: string
  description?: string
}

interface ProgressRailProps {
  steps: ProgressStep[]
  currentStep: string
  completedSteps: string[]
  onStepClick?: (stepId: string) => void
}

export function ProgressRail({ 
  steps, 
  currentStep, 
  completedSteps, 
  onStepClick 
}: ProgressRailProps) {
  const getStepIndex = (stepId: string) => steps.findIndex(s => s.id === stepId)
  const currentIndex = getStepIndex(currentStep)

  const getStepStatus = (stepId: string) => {
    if (completedSteps.includes(stepId)) return 'completed'
    if (stepId === currentStep) return 'current'
    return 'future'
  }

  return (
    <div className="relative">
      {/* Connecting line */}
      <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-dark-700" />
      
      {/* Progress line */}
      <motion.div 
        className="absolute left-6 top-8 w-0.5 bg-gradient-to-b from-brand-blue to-brand-blue-dark"
        initial={{ height: 0 }}
        animate={{ 
          height: currentIndex === -1 ? 0 : `${(currentIndex / (steps.length - 1)) * 100}%`
        }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
      />

      {/* Steps */}
      <div className="relative space-y-8">
        {steps.map((step, index) => {
          const status = getStepStatus(step.id)
          const isClickable = !!onStepClick // All nodes should be clickable
          
          return (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className={cn(
                "flex items-start gap-4 group",
                isClickable && "cursor-pointer"
              )}
              onClick={() => isClickable && onStepClick(step.id)}
            >
              {/* Step indicator */}
              <div className="relative z-10">
                <motion.div
                  className={cn(
                    "w-12 h-12 rounded-full flex items-center justify-center",
                    "border-2 transition-all duration-300",
                    status === 'completed' && "bg-gradient-to-r from-brand-blue to-brand-blue-dark border-brand-blue",
                    status === 'current' && "bg-dark-800 border-brand-blue shadow-lg shadow-brand-blue/50",
                    status === 'future' && "bg-dark-800 border-dark-700",
                    isClickable && "cursor-pointer hover:border-brand-blue/50 transition-colors"
                  )}
                  whileHover={isClickable ? { scale: 1.05 } : {}}
                  whileTap={isClickable ? { scale: 0.95 } : {}}
                >
                  {status === 'completed' ? (
                    <Check className="w-5 h-5 text-white" />
                  ) : (
                    <span className={cn(
                      "text-sm font-semibold",
                      status === 'current' ? "text-brand-blue" : "text-dark-500"
                    )}>
                      {index + 1}
                    </span>
                  )}
                </motion.div>

                {/* Pulse animation for current step */}
                {status === 'current' && (
                  <motion.div
                    className="absolute inset-0 rounded-full bg-brand-blue/30"
                    animate={{
                      scale: [1, 1.3, 1],
                      opacity: [0.5, 0, 0.5],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />
                )}
              </div>

              {/* Step content */}
              <div className="flex-1 pt-2">
                <h3 className={cn(
                  "font-semibold transition-colors",
                  status === 'completed' && "text-white",
                  status === 'current' && "text-brand-blue",
                  status === 'future' && "text-dark-500",
                  isClickable && "group-hover:text-brand-blue-light"
                )}>
                  {step.label}
                </h3>
                {step.description && (
                  <p className={cn(
                    "text-sm mt-1 transition-colors",
                    status === 'future' ? "text-dark-600" : "text-dark-400"
                  )}>
                    {step.description}
                  </p>
                )}
              </div>
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}